import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { CHANNEL_LABELS, CHANNELS, INDUSTRY_TEMPLATES, type Channel } from '@marketingbee/contracts';
import { CHANNEL_RULES } from '@marketingbee/domain/marketing';
import { Alert, Button, Card, CardContent, Checkbox, EmptyState, Field, FormError, Input, PageHeader, Select, Textarea, cn, useToast } from '@marketingbee/ui';
import { QueryView, Section } from '../components/common';
import { BrandPicker, ChannelBadge, formatInZone, pillarName, useActiveBrand, type BrandProfile, type BrandRow } from '../components/marketing';
import { errorMessage, trpc } from '../lib/trpc';

const INDUSTRY_OPTIONS = INDUSTRY_TEMPLATES.map((t) => ({ value: t.id, label: t.label }));
const WEEKDAYS: [number, string][] = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']];
const FALLBACK_ZONES = ['America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/Winnipeg', 'America/Regina', 'America/Edmonton', 'America/Vancouver', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'];

function timeZones(current: string): string[] {
  let all: string[] = FALLBACK_ZONES;
  try {
    const list = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone');
    if (list?.length) all = list;
  } catch {
    // keep the fallback
  }
  return all.includes(current) ? all : [current, ...all];
}

/** Lowercase-dash id from a name, unique against `taken`. */
function makeId(name: string, taken: readonly string[]): string {
  const base = name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'item';
  let id = base;
  for (let n = 2; taken.includes(id); n++) id = `${base}-${n}`;
  return id;
}

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

export function BrandPage() {
  const { list, brands, brand, setBrandId } = useActiveBrand();
  return (
    <QueryView query={list} label="Loading the brand">
      {() => (brand ? <BrandEditor key={`${brand.id}-${brand.updatedAt ? new Date(brand.updatedAt).getTime() : 0}`} brand={brand} picker={<BrandPicker brands={brands} value={brand.id} onChange={setBrandId} />} /> : <CreateBrand />)}
    </QueryView>
  );
}

function CreateBrand() {
  const current = trpc.workspace.current.useQuery();
  const create = trpc.brand.create.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState(current.data?.settings.industry ?? 'general');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  return (
    <div>
      <PageHeader title="Brand" description="Your Brand Brain: who you are, who you talk to, what about, and the rules every post follows." />
      <Card>
        <CardContent className="space-y-4 py-6">
          <p className="text-[15px] text-muted-foreground">Start with the basics. Picking an industry suggests audiences and content pillars you can rename or remove.</p>
          {create.error ? <FormError message={errorMessage(create.error)} /> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Brand name" required><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Industry"><Select value={industry} onChange={(e) => setIndustry(e.target.value)} options={INDUSTRY_OPTIONS} /></Field>
            <Field label="Website"><Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></Field>
          </div>
          <Field label="What you do" description="Two or three sentences in plain words. The writer uses this for every draft.">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Button
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { industry, profile: { name, website: website || null, description, timezone: current.data?.settings.timezone ?? 'America/Toronto' } },
                {
                  onSuccess: async () => {
                    success('Brand created');
                    await Promise.all([utils.brand.list.invalidate(), utils.workspace.current.invalidate()]);
                  },
                },
              )
            }
          >
            Create brand
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function BrandEditor({ brand, picker }: { brand: BrandRow; picker: ReactNode }) {
  const [p, setP] = useState<BrandProfile>(brand.profile);
  const [industry, setIndustry] = useState<string>(brand.industry ?? 'general');
  const update = trpc.brand.update.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [saveError, setSaveError] = useState<string | null>(null);
  const zones = useMemo(() => timeZones(p.timezone), [p.timezone]);
  const set = <K extends keyof BrandProfile>(key: K, value: BrandProfile[K]) => setP((x) => ({ ...x, [key]: value }));
  const dirty = JSON.stringify(p) !== JSON.stringify(brand.profile);

  const startFrom = async () => {
    try {
      const s = await utils.brand.suggestions.fetch({ industry });
      const fillAudiences = p.audiences.length === 0;
      const fillPillars = p.pillars.length === 0;
      if (!fillAudiences && !fillPillars) {
        success('Nothing replaced', 'You already have audiences and pillars. Remove them first to start from the industry suggestions.');
        return;
      }
      setP((x) => ({ ...x, audiences: fillAudiences ? s.audiences : x.audiences, pillars: fillPillars ? s.pillars : x.pillars }));
      success('Suggestions added', 'Review them, then save.');
    } catch (e) {
      error('Could not load suggestions', errorMessage(e));
    }
  };

  const save = () => {
    setSaveError(null);
    update.mutate(
      { brandId: brand.id, profile: { ...p, cadence: p.cadence.filter((c) => c.postsPerWeek > 0) } },
      {
        onSuccess: async () => {
          success('Brand saved');
          await Promise.all([utils.brand.list.invalidate(), utils.brand.get.invalidate(), utils.brand.previewWeek.invalidate(), utils.workspace.current.invalidate()]);
        },
        onError: (e) => setSaveError(errorMessage(e)),
      },
    );
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title="Brand"
        description="Your Brand Brain: who you are, who you talk to, what about, and the rules every post follows."
        actions={
          <>
            {picker}
            <Button disabled={!dirty} loading={update.isPending} onClick={save}>
              Save brand
            </Button>
          </>
        }
      />
      {saveError ? <FormError message={saveError} /> : null}

      <Section title="Basics">
        <Card>
          <CardContent className="grid gap-4 py-6 sm:grid-cols-2">
            <Field label="Brand name" required><Input value={p.name} onChange={(e) => set('name', e.target.value)} /></Field>
            <Field label="Website"><Input type="url" value={p.website ?? ''} onChange={(e) => set('website', e.target.value || null)} placeholder="https://" /></Field>
            <Field label="What you do" className="sm:col-span-2" description="The writer reads this before every draft.">
              <Textarea rows={3} value={p.description} maxLength={2000} onChange={(e) => set('description', e.target.value)} />
            </Field>
            <Field label="Voice" className="sm:col-span-2" description="How you sound, for example: warm, plain, no exclamation marks.">
              <Textarea rows={2} value={p.voice} maxLength={500} onChange={(e) => set('voice', e.target.value)} />
            </Field>
            <Field label="Time zone" description="Posting times and the calendar use this zone.">
              <Select value={p.timezone} onChange={(e) => set('timezone', e.target.value)} options={zones.map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))} />
            </Field>
            <Field label="Languages">
              <div className="flex h-10 items-center gap-5">
                {(['en', 'fr'] as const).map((l) => (
                  <Checkbox
                    key={l}
                    label={l === 'en' ? 'English' : 'French'}
                    checked={p.languages.includes(l)}
                    onCheckedChange={(v) => set('languages', v ? [...new Set([...p.languages, l])] : p.languages.length > 1 ? p.languages.filter((x) => x !== l) : p.languages)}
                  />
                ))}
              </div>
            </Field>
            <Field label="Call-to-action link" description="Where posts send people (tracked automatically).">
              <Input type="url" value={p.ctaUrl ?? ''} onChange={(e) => set('ctaUrl', e.target.value || null)} placeholder="https://" />
            </Field>
            <Field label="Mailing address" description="Required for email (CASL): it appears in the footer of every marketing email.">
              <Input value={p.mailingAddress} maxLength={300} onChange={(e) => set('mailingAddress', e.target.value)} placeholder="Business name, street, city, province, postal code" />
            </Field>
          </CardContent>
        </Card>
      </Section>

      <Section
        title="Audiences"
        description="Who you are talking to, and what bothers them."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select aria-label="Industry" className="w-56" value={industry} onChange={(e) => setIndustry(e.target.value)} options={INDUSTRY_OPTIONS} />
            <Button variant="outline" size="sm" onClick={() => void startFrom()}>
              <Sparkles /> Start from industry
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {p.audiences.map((a, i) => (
            <Card key={`${a.id}-${i}`}>
              <CardContent className="grid gap-3 py-5 sm:grid-cols-2">
                <Field label="Name"><Input value={a.name} onChange={(e) => set('audiences', p.audiences.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></Field>
                <div className="flex items-end justify-end">
                  <Button variant="ghost" size="sm" aria-label={`Remove ${a.name || 'audience'}`} onClick={() => set('audiences', p.audiences.filter((_, j) => j !== i))}>
                    <Trash2 /> Remove
                  </Button>
                </div>
                <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={a.description} onChange={(e) => set('audiences', p.audiences.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} /></Field>
                <Field label="Pains (one per line)" className="sm:col-span-2">
                  <ListInput value={a.pains} onChange={(pains) => set('audiences', p.audiences.map((x, j) => (j === i ? { ...x, pains } : x)))} />
                </Field>
              </CardContent>
            </Card>
          ))}
          {p.audiences.length === 0 ? <p className="text-[14px] text-muted-foreground">No audiences yet. Add one, or start from your industry.</p> : null}
          <Button variant="outline" size="sm" disabled={p.audiences.length >= 10} onClick={() => set('audiences', [...p.audiences, { id: makeId(`audience-${p.audiences.length + 1}`, p.audiences.map((a) => a.id)), name: '', description: '', pains: [] }])}>
            <Plus /> Add audience
          </Button>
        </div>
      </Section>

      <Section title="Content pillars" description="The topics you post about. Weight sets how often each comes up in the weekly plan.">
        <div className="space-y-3">
          {p.pillars.map((pl, i) => (
            <Card key={`${pl.id}-${i}`}>
              <CardContent className="grid gap-3 py-5 sm:grid-cols-2">
                <Field label="Name"><Input value={pl.name} onChange={(e) => set('pillars', p.pillars.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></Field>
                <Field label={`Weight: ${pl.weight}`} description={pl.weight === 0 ? 'Off: never planned.' : undefined}>
                  <input
                    type="range"
                    min={0}
                    max={10}
                    step={1}
                    value={pl.weight}
                    aria-label={`Weight for ${pl.name || 'pillar'}`}
                    className="h-10 w-full accent-[var(--color-primary)]"
                    onChange={(e) => set('pillars', p.pillars.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) } : x)))}
                  />
                </Field>
                <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={pl.description} onChange={(e) => set('pillars', p.pillars.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} /></Field>
                <div className="space-y-1 sm:col-span-2">
                  <Checkbox label="Human-only" checked={pl.humanOnly} onCheckedChange={(v) => set('pillars', p.pillars.map((x, j) => (j === i ? { ...x, humanOnly: v === true } : x)))} />
                  <p className="text-[12px] text-muted-foreground">MarketingBee plans a slot for it but never drafts it. Use it for personal stories, founder notes and anything that should be in your own words.</p>
                </div>
                <div className="flex justify-end sm:col-span-2">
                  <Button variant="ghost" size="sm" aria-label={`Remove ${pl.name || 'pillar'}`} onClick={() => set('pillars', p.pillars.filter((_, j) => j !== i))}>
                    <Trash2 /> Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {p.pillars.length === 0 ? <p className="text-[14px] text-muted-foreground">No pillars yet. Add one, or start from your industry.</p> : null}
          <Button variant="outline" size="sm" disabled={p.pillars.length >= 12} onClick={() => set('pillars', [...p.pillars, { id: makeId(`pillar-${p.pillars.length + 1}`, p.pillars.map((x) => x.id)), name: '', description: '', weight: 2, humanOnly: false }])}>
            <Plus /> Add pillar
          </Button>
        </div>
      </Section>

      <Section title="Guardrails" description="Checked on every draft before anyone sees it. Anything marked 'Must fix' cannot be approved.">
        <Card>
          <CardContent className="grid gap-4 py-6 sm:grid-cols-2">
            <Field label="Banned phrases (one per line)"><ListInput value={p.guardrails.bannedPhrases} onChange={(v) => set('guardrails', { ...p.guardrails, bannedPhrases: v })} /></Field>
            <Field label="Topics to avoid (one per line)"><ListInput value={p.guardrails.avoidTopics} onChange={(v) => set('guardrails', { ...p.guardrails, avoidTopics: v })} /></Field>
            <Field label="Approved claims (one per line)" description="Claims you can back up, such as 'Free to start'.">
              <ListInput value={p.guardrails.approvedClaims} onChange={(v) => set('guardrails', { ...p.guardrails, approvedClaims: v })} />
            </Field>
            <div className="space-y-1">
              <Checkbox label="Block unverified claims" checked={p.guardrails.blockUnverifiedClaims} onCheckedChange={(v) => set('guardrails', { ...p.guardrails, blockUnverifiedClaims: v === true })} />
              <p className="text-[12px] text-muted-foreground">Stops drafts that claim numbers, rankings or results you have not listed as approved.</p>
            </div>
          </CardContent>
        </Card>
      </Section>

      <Section title="Cadence" description="How often to post on each channel, and when. Set a channel to 0 to leave it out of the plan.">
        <Card>
          <CardContent className="divide-y divide-border py-2">
            {CHANNELS.map((ch) => (
              <CadenceRow key={ch} channel={ch} profile={p} onChange={(cadence) => set('cadence', cadence)} />
            ))}
          </CardContent>
        </Card>
      </Section>

      <div className="flex justify-end">
        <Button disabled={!dirty} loading={update.isPending} onClick={save}>Save brand</Button>
      </div>

      <PreviewWeek brand={brand} dirty={dirty} />
    </div>
  );
}

function CadenceRow({ channel, profile, onChange }: { channel: Channel; profile: BrandProfile; onChange: (c: BrandProfile['cadence']) => void }) {
  const existing = profile.cadence.find((c) => c.channel === channel);
  const row = existing ?? { channel, postsPerWeek: 0, times: ['09:30'], days: [1, 2, 3, 4, 5] };
  const [timesText, setTimesText] = useState(row.times.join(', '));
  const timesValid = timesText.split(',').map((t) => t.trim()).filter(Boolean).every((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t));
  const patch = (next: Partial<typeof row>) => {
    const merged = { ...row, ...next };
    onChange(existing ? profile.cadence.map((c) => (c.channel === channel ? merged : c)) : [...profile.cadence, merged]);
  };
  const off = row.postsPerWeek === 0;
  return (
    <div className="grid gap-3 py-4 md:grid-cols-[160px_120px_1fr_1fr] md:items-end">
      <div className="space-y-1">
        <ChannelBadge channel={channel} />
        <p className="text-[12px] text-muted-foreground">{CHANNEL_RULES[channel].countsAsPost ? 'Counts toward posts' : channel === 'email' ? 'Email (Growth)' : 'Blog (Growth)'}</p>
      </div>
      <Field label="Per week">
        <Input type="number" min={0} max={21} value={row.postsPerWeek} aria-label={`${CHANNEL_LABELS[channel]} posts per week`} onChange={(e) => patch({ postsPerWeek: Math.max(0, Math.min(21, Math.round(Number(e.target.value) || 0))) })} />
      </Field>
      <Field label="Times (HH:MM)" error={timesValid ? null : 'Use 24-hour times like 09:30, 17:00'}>
        <Input
          value={timesText}
          disabled={off}
          aria-label={`${CHANNEL_LABELS[channel]} posting times`}
          onChange={(e) => {
            setTimesText(e.target.value);
            const times = e.target.value.split(',').map((t) => t.trim()).filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)).slice(0, 6);
            if (times.length) patch({ times });
          }}
        />
      </Field>
      <fieldset className={cn('space-y-1.5', off && 'opacity-50')} disabled={off}>
        <legend className="text-[13px] font-medium">Days</legend>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {WEEKDAYS.map(([d, label]) => (
            <Checkbox
              key={d}
              label={label}
              disabled={off}
              checked={row.days.includes(d)}
              onCheckedChange={(v) => {
                const days = v ? [...new Set([...row.days, d])].sort() : row.days.filter((x) => x !== d);
                if (days.length) patch({ days });
              }}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}

/** One item per line; keeps the text as typed until blur so blank lines do not jump. */
function ListInput({ value, onChange }: { value: readonly string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join('\n'));
  return (
    <Textarea
      rows={Math.min(8, Math.max(3, value.length + 1))}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(lines(e.target.value));
      }}
      onBlur={() => setText(lines(text).join('\n'))}
    />
  );
}

function PreviewWeek({ brand, dirty }: { brand: BrandRow; dirty: boolean }) {
  const preview = trpc.brand.previewWeek.useQuery({ brandId: brand.id });
  const tz = brand.profile.timezone;
  return (
    <Section title="Preview week" description={dirty ? 'Based on the saved brand. Save to preview your changes.' : 'What the planner would schedule this week. Nothing is saved.'}>
      <QueryView query={preview}>
        {(data) =>
          data.slots.length === 0 ? (
            <Card>
              <EmptyState title="No slots" description="Set a cadence above and at least one pillar with a weight." />
            </Card>
          ) : (
            <Card>
              <CardContent className="divide-y divide-border py-2 text-[14px]">
                {data.slots.map((s) => (
                  <div key={s.index} className="flex flex-wrap items-center gap-2 py-2.5">
                    <span className="w-40 font-mono text-[13px] text-muted-foreground">{formatInZone(new Date(s.scheduledAt), tz)}</span>
                    <ChannelBadge channel={s.channel} />
                    <span>{pillarName(brand.profile, s.pillarId)}</span>
                    {s.humanOnly ? <span className="text-[12px] text-muted-foreground">· you write this one</span> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          )
        }
      </QueryView>
      {brand.profile.cadence.some((c) => c.channel === 'email' && c.postsPerWeek > 0) && !brand.profile.mailingAddress.trim() ? <Alert variant="warning">Email is in the cadence but there is no mailing address: email cannot be sent until you add one.</Alert> : null}
    </Section>
  );
}
